import { useEffect, useMemo, useState } from 'react';
import { CollectionMonthDocumentsModal } from './CollectionMonthDocumentsModal';
import {
  deriveMonthPaymentMovement,
  derivePaymentStatus,
  docMatchesDueMonth,
  fetchAccountCollectionSummary,
  formatCollectionPct,
  monthPaidPct,
  monthSettledLcy,
  monthUnpaidPct,
  type AccountCollectionMonth,
  type AccountCollectionSummary,
} from '../../lib/accountCollection';
import {
  fetchAccountCustomerComments,
  formatAccountCommentDate,
  type AccountCustomerComment,
} from '../../lib/accountComments';

interface AccountCollectionPanelProps {
  motherCode: string;
  companyName?: string;
}

function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'SGD',
    maximumFractionDigits: 2,
  });
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

function overdueAmountClass(month: AccountCollectionMonth): string {
  const classes = ['account-collection__num'];
  if (month.overdue_lcy > 0.005) {
    classes.push('account-collection__overdue-amt');
  }
  return classes.join(' ');
}

function pastMonthsCutoffIso(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function commentKey(row: AccountCustomerComment): string {
  return `${row.commentDate}:${row.customerNo}:${row.commentText.slice(0, 40)}`;
}

export function AccountCollectionPanel({ motherCode, companyName }: AccountCollectionPanelProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AccountCollectionSummary | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<AccountCollectionMonth | null>(null);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [comments, setComments] = useState<AccountCustomerComment[]>([]);
  const [showOlderComments, setShowOlderComments] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedMonth(null);
    void fetchAccountCollectionSummary(motherCode)
      .then((summary) => {
        if (cancelled) return;
        setData(summary);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err instanceof Error ? err.message : 'Failed to load collection.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [motherCode]);

  useEffect(() => {
    let cancelled = false;
    setCommentsLoading(true);
    setCommentsError(null);
    setShowOlderComments(false);
    void fetchAccountCustomerComments(motherCode)
      .then((result) => {
        if (cancelled) return;
        setComments(result.comments);
      })
      .catch((err) => {
        if (cancelled) return;
        setComments([]);
        setCommentsError(err instanceof Error ? err.message : 'Failed to load comments.');
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [motherCode]);

  const visibleMonths = useMemo(() => {
    if (!data) return [] as AccountCollectionMonth[];
    // Posting months: newest first
    return [...data.months].sort((a, b) =>
      (b.period_month || '').localeCompare(a.period_month || ''),
    );
  }, [data]);

  const paymentStatus = useMemo(() => {
    if (!data) return null;
    return derivePaymentStatus(data.summary, data.aging, data.months);
  }, [data]);

  const monthTotals = useMemo(() => {
    let unpaid = 0;
    let overdue = 0;
    let notDue = 0;
    let invoiced = 0;
    let settled = 0;
    for (const m of visibleMonths) {
      unpaid += m.unpaid_lcy;
      overdue += m.overdue_lcy;
      notDue += m.not_due_lcy;
      invoiced += m.invoiced_lcy;
      settled += monthSettledLcy(m);
    }
    const paidPct = invoiced > 0.005 ? Math.min(100, (100 * settled) / invoiced) : null;
    return { unpaid, overdue, notDue, invoiced, settled, paidPct };
  }, [visibleMonths]);

  const balanceKpi = data?.summary.unpaid_lcy ?? 0;
  const overdueKpi = data?.summary.overdue_lcy ?? monthTotals.overdue;
  const notDueKpi = data?.summary.not_due_lcy ?? monthTotals.notDue;
  const splitTotal = overdueKpi + notDueKpi;
  const overdueSharePct = splitTotal > 0.005 ? (100 * Math.max(0, overdueKpi)) / splitTotal : 0;
  const notDueSharePct = splitTotal > 0.005 ? (100 * Math.max(0, notDueKpi)) / splitTotal : 0;

  const { recentComments, olderComments } = useMemo(() => {
    const cutoff = pastMonthsCutoffIso(6);
    const recent: AccountCustomerComment[] = [];
    const older: AccountCustomerComment[] = [];
    for (const row of comments) {
      if (row.commentDate && row.commentDate >= cutoff) recent.push(row);
      else older.push(row);
    }
    return { recentComments: recent, olderComments: older };
  }, [comments]);

  const visibleComments = showOlderComments
    ? [...recentComments, ...olderComments]
    : recentComments;

  const commentsPanel = (
    <aside className="account-collection__comments" aria-label="Customer comments">
      <div className="account-collection__comments-head">
        <h3 className="account-collection__comments-title">
          Comments
          <span className="account-collection__comments-range"> (Past 6-months)</span>
        </h3>
        <span className="account-collection__comments-count">
          {commentsLoading
            ? '…'
            : showOlderComments
              ? comments.length
              : recentComments.length}
        </span>
      </div>
      {commentsLoading ? (
        <p className="account-collection__busy">Loading comments…</p>
      ) : commentsError ? (
        <p className="account-collection__error">{commentsError}</p>
      ) : comments.length === 0 ? (
        <p className="account-collection__empty">No customer comments.</p>
      ) : visibleComments.length === 0 && olderComments.length > 0 ? (
        <div className="account-collection__comments-body">
          <p className="account-collection__empty">No comments in the past 6 months.</p>
          <button
            type="button"
            className="account-side-view-all account-collection__comments-more"
            onClick={() => setShowOlderComments(true)}
          >
            Show more ({olderComments.length})
          </button>
        </div>
      ) : (
        <div className="account-collection__comments-body">
          <ul className="account-collection__comments-list">
            {visibleComments.map((row) => (
              <li key={commentKey(row)} className="account-collection__comment">
                <div className="account-collection__comment-meta">
                  <time dateTime={row.commentDate || undefined}>
                    {formatAccountCommentDate(row.commentDate)}
                  </time>
                  {row.userIds ? (
                    <span className="account-collection__comment-user">{row.userIds}</span>
                  ) : null}
                </div>
                {row.customerNo || row.customerName ? (
                  <div className="account-collection__comment-account">
                    {[row.customerNo, row.customerName].filter(Boolean).join(' · ')}
                  </div>
                ) : null}
                <p className="account-collection__comment-text">{row.commentText}</p>
              </li>
            ))}
          </ul>
          {olderComments.length > 0 ? (
            showOlderComments ? (
              <button
                type="button"
                className="account-side-view-all account-collection__comments-more"
                onClick={() => setShowOlderComments(false)}
              >
                Show less
              </button>
            ) : (
              <button
                type="button"
                className="account-side-view-all account-collection__comments-more"
                onClick={() => setShowOlderComments(true)}
              >
                Show more ({olderComments.length})
              </button>
            )
          ) : null}
        </div>
      )}
    </aside>
  );

  if (loading) {
    return (
      <div className="account-collection account-collection--with-comments">
        <div className="account-collection__main">
          <p className="account-collection__busy">Loading collection…</p>
        </div>
        {commentsPanel}
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-collection account-collection--with-comments">
        <div className="account-collection__main">
          <p className="account-collection__error">{error}</p>
        </div>
        {commentsPanel}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="account-collection account-collection--with-comments">
        <div className="account-collection__main">
          <p className="account-collection__empty">No collection data for {motherCode}.</p>
        </div>
        {commentsPanel}
      </div>
    );
  }

  const s = data.summary;
  const hasOpenBalance = s.outstanding_lcy > 0 || s.credit_open_lcy > 0;
  const pay = paymentStatus;

  if (!hasOpenBalance && visibleMonths.every((m) => m.invoiced_lcy === 0)) {
    return (
      <div className="account-collection account-collection--with-comments">
        <div className="account-collection__main">
          <p className="account-collection__empty">
            No AR activity for mother code <strong>{motherCode}</strong>
            {companyName ? ` (${companyName})` : ''}. Open AR uses full CLE; activity from prior
            calendar year onward.
          </p>
        </div>
        {commentsPanel}
      </div>
    );
  }

  return (
    <div className="account-collection account-collection--with-comments">
      <div className="account-collection__main">
        {pay ? (
          <div
            className={`account-collection__pay-status account-collection__pay-status--${pay.status}`}
          >
            <span className="account-collection__pay-status-label">Payment status</span>
            <strong>{pay.label}</strong>
          </div>
        ) : null}

        <div className="account-collection__kpis" aria-label="Collection summary">
          <div className="account-collection__kpi">
            <span className="account-collection__kpi-label">Invoiced</span>
            <strong>{formatMoney(s.invoiced_lcy)}</strong>
          </div>
          <div className="account-collection__kpi account-collection__kpi--ok">
            <span className="account-collection__kpi-label">Paid</span>
            <strong>{formatMoney(s.paid_lcy)}</strong>
          </div>
          <div className="account-collection__kpi account-collection__kpi--danger">
            <span className="account-collection__kpi-label">Balance</span>
            <strong>{formatMoney(balanceKpi)}</strong>
          </div>
          <div className="account-collection__kpi account-collection__kpi--danger">
            <span className="account-collection__kpi-label">Overdue</span>
            <strong>{formatMoney(overdueKpi)}</strong>
          </div>
          <div className="account-collection__kpi account-collection__kpi--ok">
            <span className="account-collection__kpi-label">Not due</span>
            <strong>{formatMoney(notDueKpi)}</strong>
          </div>
          <div className="account-collection__kpi">
            <span className="account-collection__kpi-label">Open CM</span>
            <strong>{formatMoney(s.credit_open_lcy)}</strong>
          </div>
        </div>

        {splitTotal > 0.005 ? (
          <section className="account-collection__balance-split" aria-label="Balance breakdown">
            <div className="account-collection__balance-split-head">
              <span className="account-collection__balance-split-title">Balance split</span>
              <strong>{formatMoney(balanceKpi)}</strong>
            </div>
            <div
              className="account-collection__balance-split-bar"
              role="img"
              aria-label={`Overdue ${formatPct(overdueSharePct)}, Not due ${formatPct(notDueSharePct)}`}
            >
              {overdueKpi > 0.005 ? (
                <div
                  className="account-collection__balance-split-seg account-collection__balance-split-seg--overdue"
                  style={{ flexGrow: Math.max(overdueSharePct, 1) }}
                />
              ) : null}
              {notDueKpi > 0.005 ? (
                <div
                  className="account-collection__balance-split-seg account-collection__balance-split-seg--not-due"
                  style={{ flexGrow: Math.max(notDueSharePct, 1) }}
                />
              ) : null}
            </div>
            <div className="account-collection__balance-split-legend">
              <div className="account-collection__balance-split-item">
                <span className="account-collection__balance-split-dot account-collection__balance-split-dot--overdue" />
                <span>Overdue</span>
                <strong>{formatMoney(overdueKpi)}</strong>
                <span className="account-collection__balance-split-pct">
                  {formatPct(overdueSharePct)}
                </span>
              </div>
              <div className="account-collection__balance-split-item">
                <span className="account-collection__balance-split-dot account-collection__balance-split-dot--not-due" />
                <span>Not due</span>
                <strong>{formatMoney(notDueKpi)}</strong>
                <span className="account-collection__balance-split-pct">
                  {formatPct(notDueSharePct)}
                </span>
              </div>
            </div>
          </section>
        ) : null}

        <div className="account-collection__table-wrap">
          <table className="account-collection__table account-collection__table--months">
            <thead>
              <tr>
                <th>Month</th>
                <th className="account-collection__num">Invoiced</th>
                <th className="account-collection__num">Paid</th>
                <th className="account-collection__num">Paid %</th>
                <th className="account-collection__num">Unpaid</th>
                <th className="account-collection__num">Unpaid %</th>
                <th className="account-collection__num">Overdue</th>
                <th className="account-collection__num">Not due</th>
                <th>Movement</th>
                <th className="account-collection__num">Open inv</th>
              </tr>
            </thead>
            <tbody>
              {visibleMonths.length === 0 ? (
                <tr>
                  <td colSpan={10} className="account-collection__empty">
                    No posting-month activity in range.
                  </td>
                </tr>
              ) : (
                visibleMonths.map((month) => {
                  const move = deriveMonthPaymentMovement(month);
                  const paidPct = monthPaidPct(month);
                  const unpaidPct = monthUnpaidPct(month);
                  return (
                    <tr
                      key={`${month.aging_bucket || month.period_month}-${month.month_label}`}
                      className={[
                        'account-collection__row--clickable',
                        month.outstanding_lcy > 0 && month.overdue_lcy > 0
                          ? 'account-collection__row--overdue'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onDoubleClick={() => setSelectedMonth(month)}
                      title="Double-click to view documents"
                    >
                      <td>{month.month_label}</td>
                      <td className="account-collection__num">
                        {formatMoney(month.invoiced_lcy)}
                      </td>
                      <td className="account-collection__num">{formatMoney(month.paid_lcy)}</td>
                      <td className="account-collection__num account-collection__pct">
                        {formatCollectionPct(paidPct)}
                      </td>
                      <td className="account-collection__num">
                        {formatMoney(month.unpaid_lcy)}
                      </td>
                      <td className="account-collection__num account-collection__pct">
                        {formatCollectionPct(unpaidPct)}
                      </td>
                      <td className={overdueAmountClass(month)}>
                        {formatMoney(month.overdue_lcy)}
                      </td>
                      <td className="account-collection__num">
                        {formatMoney(month.not_due_lcy)}
                      </td>
                      <td>
                        <span
                          className={`account-collection__move account-collection__move--${move.status}`}
                          title={move.reason}
                        >
                          {move.label}
                        </span>
                      </td>
                      <td className="account-collection__num">{month.open_invoice_count}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {selectedMonth ? (
          <CollectionMonthDocumentsModal
            title={`${selectedMonth.month_label} · documents`}
            documents={data.documents}
            matchDoc={(doc) =>
              docMatchesDueMonth(
                doc,
                selectedMonth,
                data.aging.period_end || data.meta.period_end,
              )
            }
            onClose={() => setSelectedMonth(null)}
          />
        ) : null}
      </div>

      {commentsPanel}
    </div>
  );
}
